<template>
  <div class="theme-switch">
    <button
      type="button"
      class="theme-btn theme-mode-btn"
      :aria-label="mode === 'dark' ? '切换到浅色' : '切换到深色'"
      :title="mode === 'dark' ? '切换到浅色' : '切换到深色'"
      @click.stop="flipMode"
    >
      <i class="mdi" :class="mode === 'dark' ? 'mdi-weather-sunny' : 'mdi-weather-night'"></i>
    </button>
    <button
      type="button"
      class="theme-btn"
      :class="{ open: open }"
      aria-label="切换配色主题"
      :title="'当前主题：' + current.name"
      @click.stop="open = !open"
    >
      <span class="theme-dot" :style="{ background: current.colors[0] }"></span>
      <i class="mdi mdi-palette-outline"></i>
    </button>
    <transition name="theme-pop">
      <div v-show="open" class="theme-panel" @click.stop>
        <div class="theme-panel-title">明暗</div>
        <div class="theme-modes">
          <button type="button" :class="{ active: mode === 'light' }" @click="setMode('light')">
            <i class="mdi mdi-weather-sunny"></i> 浅色
          </button>
          <button type="button" :class="{ active: mode === 'dark' }" @click="setMode('dark')">
            <i class="mdi mdi-weather-night"></i> 深色
          </button>
        </div>
        <div class="theme-panel-title">配色主题</div>
        <button
          v-for="t in themes"
          :key="t.key"
          type="button"
          class="theme-item"
          :class="{ active: t.key === value }"
          @click="pick(t.key)"
        >
          <span class="theme-swatches">
            <span v-for="c in t.colors" :key="c" :style="{ background: c }"></span>
          </span>
          <span class="theme-meta">
            <span class="theme-name">{{ t.name }}</span>
            <span class="theme-note">{{ t.note }}</span>
          </span>
          <i v-if="t.key === value" class="mdi mdi-check"></i>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { THEMES, THEME_STORAGE_KEY, MODE_STORAGE_KEY, getTheme, applyTheme, getMode, applyMode, toggleMode } from "@/assets/js/theme";

export default {
  name: "ThemeSwitcher",
  data() {
    return {
      open: false,
      value: getTheme(),
      mode: getMode(),
      themes: THEMES
    };
  },
  computed: {
    current() {
      return this.themes.find(t => t.key === this.value) || this.themes[0];
    }
  },
  mounted() {
    document.addEventListener("click", this.close);
    document.addEventListener("keydown", this.onKey);
    // 另一个标签页切了主题，这里同步跟上
    window.addEventListener("storage", this.onStorage);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.close);
    document.removeEventListener("keydown", this.onKey);
    window.removeEventListener("storage", this.onStorage);
  },
  methods: {
    pick(key) {
      this.value = applyTheme(key);
      this.open = false;
    },
    setMode(m) {
      this.mode = applyMode(m);
    },
    flipMode() {
      this.mode = toggleMode();
    },
    close() {
      this.open = false;
    },
    onKey(e) {
      if (e.key === "Escape") this.open = false;
    },
    onStorage(e) {
      if (e.key === THEME_STORAGE_KEY && e.newValue && e.newValue !== this.value) {
        this.value = applyTheme(e.newValue);
      }
      if (e.key === MODE_STORAGE_KEY && e.newValue && e.newValue !== this.mode) {
        this.mode = applyMode(e.newValue);
      }
    }
  }
};
</script>

<style scoped>
.theme-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.theme-mode-btn {
  padding: 5px 7px;
}
.theme-modes {
  display: flex;
  gap: 6px;
  padding: 0 8px 10px;
}
.theme-modes button {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 0;
  font-size: 13px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-mid);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.theme-modes button:hover {
  background: var(--bg-alt);
}
.theme-modes button.active {
  background: var(--accent-wash);
  border-color: var(--accent);
  color: var(--accent-deep);
}
.theme-modes .mdi {
  font-size: 15px;
}
.theme-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: var(--ink-mid);
  cursor: pointer;
  line-height: 1;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}
.theme-btn:hover,
.theme-btn.open {
  border-color: var(--border);
  background: var(--bg-card);
  color: var(--ink);
}
.theme-btn .mdi {
  font-size: 18px;
}
.theme-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--bg-card);
}
.theme-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 232px;
  padding: 8px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: 0 10px 30px -8px rgba(20, 20, 19, 0.18);
  z-index: 20;
  text-align: left;
}
.theme-panel-title {
  font-size: 12px;
  letter-spacing: 1px;
  color: var(--ink-faint);
  padding: 6px 8px 8px;
}
.theme-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px 8px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.theme-item:hover {
  background: var(--bg-alt);
}
.theme-item.active {
  background: var(--accent-wash);
}
.theme-swatches {
  display: inline-flex;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--border-soft);
  margin-right: 10px;
  flex: none;
}
.theme-swatches span {
  width: 14px;
  height: 22px;
  display: block;
}
.theme-meta {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.theme-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
}
.theme-note {
  font-size: 11.5px;
  color: var(--ink-faint);
  line-height: 1.3;
  margin-top: 1px;
}
.theme-item .mdi-check {
  color: var(--accent);
  font-size: 16px;
}
.theme-pop-enter-active,
.theme-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.theme-pop-enter,
.theme-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
